import Scheme from "./scheme/Scheme";
import BreadcrumbListScheme, { Breadcrumb } from "./scheme/BreadcrumbListScheme";
import NewsArticleScheme from "./scheme/NewsArticleScheme";
import AboutPageScheme from "./scheme/AboutPageScheme";
import BusinessEventScheme from "./scheme/BusinessEventScheme";
import OrganizationScheme from "./scheme/OrganizationScheme";
import FaqPageScheme from "./scheme/FaqPageScheme/FaqPageScheme";
import { benefitsFaqs } from "./scheme/FaqPageScheme/data";
import { numbersList } from "sections/Company/ContactUs/data";

type TPostSchema = {
  title: string;
  description: string;
  image: string;
  author: string;
  path: string;
  publishDate: string;
  post?: any
};

type FreightChildrenPagesParams = {
  title: string;
  path: string;
};

const defaultSchema = (title: string, description: string, path: string) => {
  return new Scheme(path)
    .organization()
    .set('name', `${title} | Digital Freight Alliance`)
    .set('url', Scheme.url(path))
    .set('logo', "https://content.presspage.com/uploads/2451/1920_dfalliancelogo.jpg")
    .set('image', "https://content.presspage.com/uploads/2451/1920_dfalliancelogo.jpg")
    .set('description', description)
    .getScheme()
}

const getSchema = (title: string, description: string, path: string, name: string, image?: string) => {
  const scheme = new Scheme(path)
    .webPage()
    .set('alternateName', `${title} | Digital Freight Alliance`)
    .set('description', description)
    .setBreadcrumb([{ name: 'Home', url: '/' }, { name, url: path }])
  if (image) scheme.set('image', Scheme.url(image))

  return scheme.getScheme()
};

const postSchema = ({ title, description, image, author, path, publishDate, post }: TPostSchema) => {
  const breadcrumb: Breadcrumb[] = [
    { name: 'Home', url: '/' },
    { name: 'Blogs & Updates', url: '/blog' },
  ]

  if (path.replace('/blog', '').length) {
    breadcrumb.push({ name: title, url: path })
  }

  const scheme = new NewsArticleScheme(path)
    .setMainEntityOfPage()
    .set('headline', title)
    .set('description', description)
    .setBreadcrumb(breadcrumb)

  if (image) scheme.set('image', Scheme.url(image));
  if (author) scheme.setAuthor(author);
  if (publishDate) scheme.set('datePublished', new Date(publishDate).toJSON());
  if (post) scheme.set('dateModified', new Date(post.updated_at).toJSON())

  return scheme.getScheme();
};

export const webSchemas = {
  '/': defaultSchema,

  '/membership': (title: string, description: string, path: string) => getSchema(title, description, path, 'Membership', '/images/pages/Membership/membership.webp'),

  '/benefits': (title: string, description: string, path: string) => getSchema(title, description, path, 'Benefits', '/images/pages/Benefits/benefits.webp'),
  '/benefits/support': (title: string, description: string, path: string) => getSchema(title, description, path, 'Support', '/images/pages/Benefits/Support/support.webp'),
  '/benefits/shipping-leads': (title: string, description: string, path: string) => getSchema(title, description, path, 'Shipping Leads', '/images/pages/Benefits/ShippingLeads/shipping-leads.webp'),
  // '/benefits/payment-protection': (title: string, description: string, path: string) => getSchema(title, description, path, 'Payment Protection', '/images/pages/Benefits/PaymentProtection/payment-protection.webp'),
  '/benefits/networking': (title: string, description: string, path: string) => getSchema(title, description, path, 'Networking', '/images/pages/Benefits/Networking/networking.webp'),
  '/benefits/brand-reliability': (title: string, description: string, path: string) => getSchema(title, description, path, 'Brand Reliability', '/images/pages/Benefits/BrandReliability/brand-reliability.webp'),
  '/benefits/member-directory': (title: string, description: string, path: string) => getSchema(title, description, path, 'Member Directory', '/images/pages/Benefits/MemberDirectory/member-directory.webp'),

  '/digital-tools': (title: string, description: string, path: string) => getSchema(title, description, path, 'Digital Tools', '/images/pages/DigitalTools/digital-tools.webp'),

  // '/digital-tools/cargoes-flow': () => new Scheme('/digital-tools/cargoes-flow')
  //   .webPage()
  //   .set('alternateName', 'CARGOES Flow')
  //   .set('description', 'The container tracking tool that tracks ocean shipments across 100s of shipping lines, all in one easy to use interface. See the status of your shipments in real time.')
  //   .set('image', Scheme.url('/images/pages/DigitalTools/CargoesFlow/cargoes-flow.webp'))
  //   .set('keywords', ["Container Tracking", "Shipping Tracking"])
  //   .set('significantLink', Scheme.url('/digital-tools/cargoes-flow#what-is'))
  //   .setBreadcrumb([{ name: 'Home', url: '/' }, { name: 'Digital Tools', url: '/digital-tools' }, { name: 'CARGOES Flow', url: '/digital-tools/cargoes-flow' },])
  //   .getScheme(),

  '/digital-tools/tracking-system': () => new Scheme('/digital-tools/tracking-system')
    .webPage()
    .set('alternateName', 'Tracking System')
    .set('description', 'The ultimate cargo tracking tool that works on any desktop or mobile device. Let your customers know where their cargo is and view it on the map without having to call you! Get access to tracking data from more than 160 shipping carriers.')
    .set('image', Scheme.url('/images/pages/DigitalTools/TrackingSystem/tracking-system.webp'))
    .set('keywords', ["Cargo Tracking System", "Shipping Tracking System"])
    .setBreadcrumb([{ name: 'Home', url: '/' }, { name: 'Digital Tools', url: '/digital-tools' }, { name: 'Tracking System', url: '/digital-tools/tracking-system' },])
    .getScheme(),

  '/digital-tools/ship-schedules': () => new Scheme('/digital-tools/ship-schedules')
    .webPage()
    .set('alternateName', 'Shipping Schedules')
    .set('description', 'Manage and view real-time ship schedules — plan routes, optimize logistics & stay on schedule. DF Alliance digital tools for logistics efficiency.')
    .set('image', Scheme.url('/images/pages/DigitalTools/ShipSchedules/ship-schedules.webp'))
    .set('keywords', ["ship schedules", "shipping schedules"])
    .setBreadcrumb([{ name: 'Home', url: '/' }, { name: 'Digital Tools', url: '/digital-tools' }, { name: 'Ship Schedules', url: '/digital-tools/ship-schedules' },])
    .getScheme(),

  '/digital-tools/logistics-explorer': () => new Scheme('/digital-tools/logistics-explorer')
    .webPage()
    .set('alternateName', 'Logistics Explorer')
    .set('description', 'Promote your freight rates, generate hot shipping leads, and grow your business with DF Alliance. Logistics Explorer helps forwarders to engage shippers and boost global coverage of tariffs.')
    .set('image', Scheme.url('/images/pages/DigitalTools/LogisticsExplorer/logistics-explorer.webp'))
    .set('keywords', ["logistics explorer"])
    .setBreadcrumb([{ name: 'Home', url: '/' }, { name: 'Digital Tools', url: '/digital-tools' }, { name: 'Logistics Explorer', url: '/digital-tools/logistics-explorer' },])
    .getScheme(),

  '/resources': (title: string, description: string, path: string) => getSchema(title, description, path, 'Resources', '/images/pages/Resources/resources.webp'),
  '/resources/terms-and-conditions': (title: string, description: string, path: string) => getSchema(title, description, path, 'Terms and Conditions', '/images/pages/Resources/TermsAndConditions/terms-and-conditions.webp'),
  '/resources/privacy-policy': (title: string, description: string, path: string) => getSchema(title, description, path, 'Privacy Policy', '/images/pages/Resources/PrivacyPolicy/privacy-policy.webp'),
  '/resources/bill-of-lading': (title: string, description: string, path: string) => getSchema(title, description, path, 'Bill of Lading', '/images/pages/Resources/BillOfLading/bill-of-lading.webp'),
  '/resources/demurrage-and-detention': (title: string, description: string, path: string) => getSchema(title, description, path, 'Demurrage and Detention', '/images/pages/Resources/DemurrageAndDetention/demurrage-and-detention.webp',),
  '/resources/freight-templates': (title: string, description: string, path: string) => getSchema(title, description, path, 'Freight Templates', '/images/pages/Resources/FreightTemplates/freight-templates.webp'),
  '/resources/freight-templates/bill-of-lading': (title: string, description: string, path: string) => getSchema(title, description, path, 'Bill of Lading Forms and Templates', '/images/pages/Resources/FreightTemplates/BillOfLading/bill-of-lading.webp'),
  '/resources/freight-templates/commercial-invoice': (title: string, description: string, path: string) => getSchema(title, description, path, 'Commercial Invoice Forms and Templates', '/images/pages/Resources/FreightTemplates/CommercialInvoice/commercial-invoice.webp'),
  '/resources/freight-templates/packing-list': (title: string, description: string, path: string) => getSchema(title, description, path, 'Packing List Forms and Templates', '/images/pages/Resources/FreightTemplates/PackingList/packing-list.webp'),
  '/resources/freight-templates/sli': (title: string, description: string, path: string) => getSchema(title, description, path, 'SLI Forms and Templates', '/images/pages/Resources/FreightTemplates/ShippersLetterOfInstruction/shippers-letter-of-instruction.webp'),
  '/resources/freight-templates/dangerous-goods-declaration': (title: string, description: string, path: string) => getSchema(title, description, path, 'Dangerous Goods Declaration Forms and Templates', '/images/pages/Resources/FreightTemplates/DangerousGoodsDeclaration/dangerous-goods-declaration.webp'),
  '/resources/freight-templates/freight-quotation': (title: string, description: string, path: string) => getSchema(title, description, path, 'Freight Quotation Forms and Templates', '/images/pages/Resources/FreightTemplates/FreightQuotation/freight-quotation.webp'),
  '/resources/freight-templates/certificate-of-origin': (title: string, description: string, path: string) => getSchema(title, description, path, 'Certificate of Origin Forms and Templates', '/images/pages/Resources/FreightTemplates/CertificateOfOrigin/certificate-of-origin.webp'),
  '/resources/freight-templates/proforma-invoice': (title: string, description: string, path: string) => getSchema(title, description, path, 'Proforma Invoice Forms and Templates', '/images/pages/Resources/FreightTemplates/ProformaInvoice/proforma-invoice.webp'),

  '/events': (title: string, description: string, path: string) => getSchema(title, description, path, 'Events', '/images/pages/Events/events.webp'),
  '/events/members-meeting': (title: string, description: string, path: string) => new BusinessEventScheme(path)
    .setName(title).setUrl(path).setDescription(description)
    .setStartDate('19 December 2024')
    .setOffers('2024-10-19')
    .setLocation('online', 'https://crmmarketing.powerappsportals.com/dfallianceinvitepage/')
    .getScheme(),
  '/events/global-freight-summit': (title: string, description: string, path: string) => new BusinessEventScheme(path)
    .setName(title).setUrl(path).setDescription(description)
    .setStartDate('18 November 2024').setEndDate('19 November 2024')
    .setOffers('2024-09-18')
    .setLocation('Dubai Exhibition Centre, Expo City')
    .getScheme(),
  '/events/future-of-freight': (title: string, description: string, path: string) => new BusinessEventScheme(path)
    .setName(title).setUrl(path).setDescription(description)
    .setStartDate('19 July 2022, 15:00 UTC+7 (GMT+7)')
    .setOffers('2022-04-19')
    .setLocation('online', 'https://www.youtube.com/watch?v=fRpK-6Ck9AY')
    .getScheme(),
  '/events/1st-dfa-annual-conference': (title: string, description: string, path: string) => new BusinessEventScheme(path)
    .setName(title).setUrl(path).setDescription(description)
    .setStartDate('10 March 2022').setEndDate('11 March 2022')
    .setOffers('2022-04-19')
    .setLocation('Dubai Exhibition Centre, Expo City')
    .getScheme(),

  '/company/vision-strategy': (title: string, description: string, path: string) => new AboutPageScheme(path)
    .setMainEntityOfPage()
    .setName(title)
    .setUrl(path)
    .setDescription(description)
    .getScheme(),
  '/company/contact-us': (title: string, description: string, path: string) => new OrganizationScheme(path)
    .setUrl('/')
    .setContactPoint(numbersList.map(({ phone }) => phone))
    .getScheme(),
  '/company/membership-regulations': (title: string, description: string, path: string) => getSchema(title, description, path, 'Membership Regulations', '/images/pages/Company/MembershipRegulations/membership-regulations.webp'),
  '/company/code-of-ethics': (title: string, description: string, path: string) => getSchema(title, description, path, 'Code of Ethics', '/images/pages/Company/CodeOfEthics/code-of-ethics.webp'),
  // '/company/blacklist': (title: string, description: string, path: string) => getSchema(title, description, path, 'Blacklist', '/images/pages/Company/Blacklist/blacklist.webp'),
  '/company/faqs': (title: string, description: string, path: string) => getSchema(title, description, path, 'FAQs', '/images/pages/Company/Faqs/faqs.webp'),

  '/services/trade-finance': () => new Scheme('/services/trade-finance')
    .webPage()
    .set('alternateName', 'Trade Finance')
    .set('description', 'Creating access to affordable finance for businesses of all sizes.')
    .set('image', Scheme.url('/images/pages/Services/TradeFinance/trade-finance.webp'))
    .set('keywords', ["Trade Finance", "Commercial Finance"])
    .set('significantLink', 'https://login.dpworld.com/auth/realms/DPWorld/protocol/openid-connect/auth?state=eyJ1dG1fc291cmNlIjoiIiwidXRtX21lZGl1bSI6IiIsInV0bV9jYW1wYWlnbiI6IiIsInBhZ2UtbmFtZSI6ImZpbmFuY2UiLCJidXR0b24tbmFtZSI6InZpc2l0LWNhcmdvZXMtZmluYW5jZSJ9&client_id=CF&response_type=code&redirect_uri=https://finance.dpworld.com/home/def')
    .setBreadcrumb([{ name: 'Home', url: '/' }, { name: 'Trade Finance', url: '/services/trade-finance' },])
    .getScheme(),

  '/auth/sign-in/greetings': (title: string, description: string, path: string) => getSchema(title, description, path, 'Greetings', '/images/pages/Auth/SignIn/Greetings/greetings.webp'),

  '/feedback/experience-survey': (title: string, description: string, path: string) => getSchema(title, description, path, 'Experience Survey'),
  // '/feedback/expectations-survey': (title: string, description: string, path: string) => getSchema(title, description, path, 'Expectations Survey'),



  '/partners/erp-enquiry': (title: string, description: string, path: string) => getSchema(title, description, path, 'ERP Enquiry'),

  '/freight-dictionary': (title: string, _: string, path: string) => new BreadcrumbListScheme(path)
    .setItemListElement([
      { name: 'Home', url: '/' },
      { name: title, url: '/freight-dictionary' },
    ]).getScheme(),
  freightDictionaryChildren: ({ path, title }: FreightChildrenPagesParams) => new BreadcrumbListScheme(path)

    .setItemListElement([
      { name: 'Home', url: '/' },
      { name: 'Freight Dictionary | All Freight Terms with Explanations - DF Alliance', url: '/freight-dictionary' },
      { name: title, url: path },
    ]).getScheme(),

  '/freight-forwarder': (title: string) => new BreadcrumbListScheme()
    .setItemListElement([
      { name: 'Home', url: '/' },
      { name: title, url: '/freight-forwarder' },
    ]).getScheme(),
  freightForwarderChildren: ({ path, title }: FreightChildrenPagesParams) => new BreadcrumbListScheme()
    .setItemListElement([
      { name: 'Home', url: '/' },
      { name: 'How to Become a Freight Forwarder | DF Alliance', url: '/freight-forwarder' },
      { name: title, url: path },
    ], true).getScheme(),

  post: postSchema,

  // Schemas for Benefits pages:
  ...Object.fromEntries(
    Object.keys(benefitsFaqs).map((path) => [
      path,
      () => {
        const scheme = new FaqPageScheme(path);
        benefitsFaqs[path].forEach(({ question, answer }) => {
          scheme.addQuestion(question, answer);
        });
        return scheme.getScheme();
      },
    ])
  ),
};
